// Attract's pages in the five steps (docs/specs/MODULE_NAVIGATION.md § Attract). The Attract agent owns this file.
// Each page shows only its own sections (screens/attract/AttractAccounts, AttractRules and AttractWork).
// Screens are loaded with screen() (see ./screen.ts), never imported directly.
import { fiveSteps, type SteppedModule } from "./types";
import { screen } from "./screen";

const accounts = () => import("../screens/attract/AttractAccounts");
const WebsiteAccount = screen(accounts, "WebsiteAccount");
const BlogAccount = screen(accounts, "BlogAccount");
const LinkedInAccount = screen(accounts, "LinkedInAccount");
const MetaAccount = screen(accounts, "MetaAccount");
const VideoAccount = screen(accounts, "VideoAccount");
const NewsletterAccount = screen(accounts, "NewsletterAccount");
const AdAccounts = screen(accounts, "AdAccounts");
const ListingsAccount = screen(accounts, "ListingsAccount");
const BrandAccount = screen(accounts, "BrandAccount");

const rules = () => import("../screens/attract/AttractRules");
const AudiencesRules = screen(rules, "AudiencesRules");
const VolumeRules = screen(rules, "VolumeRules");
const TopicsRules = screen(rules, "TopicsRules");
const CtaRules = screen(rules, "CtaRules");
const AdsRules = screen(rules, "AdsRules");
const VideoConsentRules = screen(rules, "VideoConsentRules");
const ApprovalsRules = screen(rules, "ApprovalsRules");

const work = () => import("../screens/attract/AttractWork");
const PastPerformance = screen(work, "PastPerformance");
const WebPresenceResearch = screen(work, "WebPresenceResearch");
const KeywordsResearch = screen(work, "KeywordsResearch");
const PostsActions = screen(work, "PostsActions");
const WebsiteActions = screen(work, "WebsiteActions");
const LongFormActions = screen(work, "LongFormActions");
const NewsletterActions = screen(work, "NewsletterActions");
const AdsActions = screen(work, "AdsActions");
const ListingsActions = screen(work, "ListingsActions");
const PipelineActions = screen(work, "PipelineActions");
const AttractResults = screen(work, "AttractResults");
const AttractHealth = screen(work, "AttractHealth");
// Upgrades Oct 2026 (Wave 0): pages a later job builds; each says "Coming soon" until then.
const RefreshArticles = screen(() => import("../screens/attract/RefreshArticles"), "RefreshArticles");
const PostResults = screen(() => import("../screens/attract/PostResults"), "PostResults");

export const attract: SteppedModule = {
  key: "attract",
  label: "Attract",
  layout: "steps",
  paused: "Brings people to you: your website, posts, newsletter and listings, so the right businesses get in touch.",
  tabs: fiveSteps({
    accounts: [
      { slug: "website", label: "Website", step: "0.9", render: () => <WebsiteAccount /> },
      { slug: "blog", label: "Blog", step: "1.7", render: () => <BlogAccount /> },
      { slug: "linkedin", label: "LinkedIn", step: "1.20", render: () => <LinkedInAccount /> },
      { slug: "meta", label: "Instagram and Facebook", step: "0.12", render: () => <MetaAccount /> },
      { slug: "video", label: "YouTube and TikTok", step: "1.39", render: () => <VideoAccount /> },
      { slug: "newsletter", label: "Newsletter", step: "0.11", render: () => <NewsletterAccount /> },
      { slug: "ads", label: "Ad accounts", step: "0.17", render: () => <AdAccounts /> },
      { slug: "listings", label: "Listings", step: "0.23", render: () => <ListingsAccount /> },
      { slug: "brand", label: "Brand and identity", step: "0.13", render: () => <BrandAccount /> },
    ],
    rules: [
      { slug: "audiences", label: "Audiences, routes and offers", step: "1.19", render: () => <AudiencesRules /> },
      { slug: "volume", label: "Volume and mix", step: "1.20", render: () => <VolumeRules /> },
      { slug: "topics", label: "Topics and news searches", step: "1.2", render: () => <TopicsRules /> },
      { slug: "ctas", label: "Calls to action", step: "1.49", render: () => <CtaRules /> },
      { slug: "ads", label: "Ads", step: "1.26", render: () => <AdsRules /> },
      { slug: "video-consent", label: "Video consent", step: "1.40", render: () => <VideoConsentRules /> },
      { slug: "approvals", label: "Approvals", step: "E13", render: () => <ApprovalsRules /> },
    ],
    research: [
      {
        slug: "news",
        label: "News and topics",
        step: "1.1",
        comingSoon: "Ogma will sweep the news and your topics each week and suggest what to write about, with the sources it read.",
      },
      { slug: "past-performance", label: "Past performance", step: "1.55", render: () => <PastPerformance /> },
      { slug: "web-presence", label: "Web presence", step: "0.14", render: () => <WebPresenceResearch /> },
      { slug: "keywords", label: "Keywords", step: "1.34", render: () => <KeywordsResearch /> },
    ],
    // Posts first: approving posts is what an owner does most often here.
    actions: [
      { slug: "posts", label: "Posts", step: "1.21", render: () => <PostsActions /> },
      { slug: "long-form", label: "Articles and blog", step: "1.2", render: () => <LongFormActions /> },
      { slug: "website", label: "Website", step: "0.20", render: () => <WebsiteActions /> },
      { slug: "newsletter", label: "Newsletter", step: "1.9", render: () => <NewsletterActions /> },
      { slug: "ads", label: "Ads", step: "1.28", render: () => <AdsActions /> },
      { slug: "listings", label: "Listings to claim", step: "1.43", render: () => <ListingsActions /> },
      { slug: "pipeline", label: "Pipeline", step: "1.6", render: () => <PipelineActions /> },
      { slug: "refresh", label: "Refresh old articles", step: "1.36", render: () => <RefreshArticles /> },
    ],
    performance: [
      { slug: "results", label: "Results", step: "1.19", render: () => <AttractResults /> },
      { slug: "post-results", label: "Per-post results", step: "1.22", render: () => <PostResults /> },
      { slug: "health", label: "Health", step: "0.24", render: () => <AttractHealth /> },
    ],
  }),
  taskHomes: {
    // posts on Posts, articles, pitches and print packs on Articles and blog, issues on Newsletter (each page filters)
    approve_content: ["/attract/actions/posts", "/attract/actions/long-form", "/attract/actions/newsletter"],
    syndication_pack: ["/attract/actions/newsletter"],
    // 1.6 a post Ogma could not confirm on Instagram or Facebook: did it go out?
    meta_post_check: ["/attract/actions/posts"],
    // Upgrades Oct 2026: 1.36 an article to refresh, 1.69 posts to comment on, 1.37 an AI answer to fix
    refresh_article: ["/attract/actions/refresh"],
    engage_linkedin: ["/attract/actions/posts"],
    ai_answer_fix: ["/attract/research/web-presence"],
  },
};
